import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useContext, useMemo } from "react";
import { getUpcomingEvents, getEventsFromIcsFiles } from "src/utils/Utils.js";
import { useGetUserEvents, useCalendarsByTags } from "src/services";
import { AuthContext } from "src/utils/AuthContext.js";
import { useCalendarSections } from "src/services/queries/useGetCalendarSections.js";
import { useUpdateTags } from "src/services/mutations/useUpdateTags.js";
import { useParams, useSearchParams } from "react-router-dom";
import { useSessionStorage } from "./useSessionStorage.js";
import onboardingTags from "src/onboarding-tags.json";

export const useTrendingContent = () => {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [initialLoad, setInitialLoad] = useState(false);
  const [addedCalendars, setAddedCalendars] = useState([]);
  const [isAddedLoading, setIsAddedLoading] = useState(true);
  const trendingTags = useMemo(() => Object.values(onboardingTags).flat(), []);
  const { tag } = useParams();
  
  // Initialize savedTags from user.tags
  const savedTags = useMemo(() => user?.tags || [], [user?.tags]);
  
  // tagsFilter only for selection/filtering
  const [tagsFilter, setTagsFilter] = useSessionStorage(
    "tagsFilter",
    tag && trendingTags.includes(tag) ? [tag] : [trendingTags[0]]
  );

  // Modify the existing effect to only handle the upgrade case
  useEffect(() => {
    const noUserTags = user && (!user?.tags || user?.tags?.length === 0);
    if (noUserTags) {
      updateTags({ tags: [...tagsFilter] });
    }
  }, [user]);

  const { data: dataUserEvents, refetch: refetchUserEvents } = useGetUserEvents(
    {
      user,
    },
    {
      enabled: Boolean(user),
    }
  );
  useEffect(() => {
    if (Boolean(user)) {
      refetchUserEvents();
    }
  }, [user]);

  const { data: upcomingAddedEvents, refetch: refetchUpcomingAddedEvents } =
    useQuery(
      [`upcoming-added-events`, dataUserEvents],
      async () => {
        setInitialLoad(false);
        setAddedCalendars(dataUserEvents);
        if (dataUserEvents.length === 0) {
          setIsAddedLoading(false);
          return [];
        }
        let addedEvents = [];
        const callback = (events) =>
          (addedEvents = [...addedEvents, ...events]);
        await getEventsFromIcsFiles(dataUserEvents, callback);
        setIsAddedLoading(false);
        return getUpcomingEvents(addedEvents, 100, true);
      },
      {
        initialData: [],
        enabled: !!dataUserEvents,
        refetchOnMount: true,
      }
    );

  useEffect(() => {
    if (dataUserEvents) {
      refetchUpcomingAddedEvents();
    }
  }, [dataUserEvents]);

  const { data: calendarSections, isLoading: isCalendarSectionsLoading } =
    useCalendarSections();

  let {
    data: searchByTagsData,
    isFetching: isLoadingCalendarsByTags,
    isFetchingNextPage: isFetchingNextPageCalendarsByTags,
    fetchNextPage: fetchSearchByTagsNextPage,
  } = useCalendarsByTags({ tags: tagsFilter, pageSize: 10 });
  if (isFetchingNextPageCalendarsByTags) {
    isLoadingCalendarsByTags = false;
  }
  const {
    subscriberAZCalendars = null,
    leaderboardCalendars = null,
    relatedTags = [],
    hasMoreSearchByTagsPages,
  } = searchByTagsData || {};
  const isStaffPicks = tagsFilter[0] === "Staff Picks";
  const { mutateAsync: updateTags } = useUpdateTags();

  useEffect(() => {
    if (
      searchParams.tag === "discover" ||
      new URLSearchParams(window.location.search).get("tab") === "discover"
    ) {
      setInitialLoad(false);
    }
  }, [searchParams]);

  return {
    refetchUserEvents,
    addedCalendars,
    setAddedCalendars,
    isAddedLoading,
    upcomingAddedEvents,
    initialLoad,
    setInitialLoad,
    isTrendingLoading: isCalendarSectionsLoading,
    calendarSections,
    tagsFilter,
    setTagsFilter,
    savedTags,
    isStaffPicks,
    subscriberAZCalendars,
    leaderboardCalendars,
    isLoadingCalendarsByTags,
    relatedTags,
    hasMoreSearchByTagsPages,
    fetchSearchByTagsNextPage,
  };
};
